import type { VoiceEndpoint } from "../endpoint";
import { loadLocalApiUrl } from "../endpoint";
import type { TtsModelOpt } from "../ttsCatalog";
import { gptSovitsFromEndpoint, gptSovitsTts } from "./gptSovits";
import { openaiTts } from "./openaiTts";
import { suiteTts } from "./suite";
import type { TtsRequest, TtsResult } from "./types";

export type { TtsRequest, TtsResult } from "./types";
export { SUITE_TTS_COST_HINT_USD } from "./suite";

function isDedicatedMode(mode: string): boolean {
  return mode === "binder" || mode === "gpuforge" || mode === "forge_endpoint";
}

/**
 * Route TTS to Suite (relay → CF origin) / dedicated GPU endpoint / local VoiceForge engine.
 */
export async function routeTts(opts: {
  mode: "suite" | "binder" | "local" | "gpuforge" | "forge_endpoint" | string;
  endpoint: VoiceEndpoint | null;
  request: TtsRequest;
  sessionToken?: string | null;
  /** Suite catalog for cost hints */
  catalog?: TtsModelOpt[];
}): Promise<TtsResult> {
  if (opts.mode === "suite") {
    return suiteTts(opts.request, opts.catalog);
  }

  if (isDedicatedMode(opts.mode)) {
    const ep = opts.endpoint;
    if (!ep?.endpointUrl) {
      return {
        ok: false,
        error:
          "No dedicated voice endpoint. Connect a Forge endpoint, then Generate on Suite.",
        backend: "openai-tts",
      };
    }
    if (ep.bindKind === "gpt-sovits") {
      return gptSovitsFromEndpoint(ep, opts.request);
    }
    if (ep.bindKind === "openai-stt") {
      return {
        ok: false,
        error:
          "Bound endpoint is STT (whisper). Switch kind to openai-tts for speech, or use STT panel later.",
        backend: "openai-stt",
      };
    }
    // default openai-tts / comfy audio / unknown → try OpenAI speech shape
    return openaiTts(ep, opts.request, opts.sessionToken);
  }

  // local VoiceForge engine (self-hosted api_v2)
  const local = loadLocalApiUrl();
  if (opts.endpoint?.bindKind === "gpt-sovits" && opts.endpoint.endpointUrl) {
    return gptSovitsFromEndpoint(opts.endpoint, opts.request);
  }
  return gptSovitsTts(local, opts.request);
}
